import { useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import {
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
  Table,
} from 'reactstrap';
import { useMutation, useQuery } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import Icon from '@mdi/react';
import { mdiEyeOffOutline, mdiEyeOutline, mdiKeyVariant } from '@mdi/js';
import { toast } from 'react-toastify';
import {
  defaultDashboard,
  initialValues,
  userStatusOptions,
  validationSchema,
} from './constants';
import './EditUser.scss';
import {
  fetchCompanyList,
  fetchRoleList,
} from '../../../store/apis/adminService';
import { fetchHrCompanyList } from '../../../store/apis/commonService';
import { createUser } from '../../../store/apis/userService';
import { encode } from '../../../utils/encrypt.util';

function EditUser({ isOpen, toggle, onUserUpdated, userData, refetch }) {
  const [showPassword, setShowPassword] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
    trigger,
  } = useForm({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const { fields, update } = useFieldArray({
    control,
    name: 'isDefaultCompanies',
  });

  const { data: companyOptions = [] } = useQuery({
    queryKey: ['companyList'],
    queryFn: fetchCompanyList,
    enabled: isOpen, // only fetch when modal is open
  });

  const { data: roleOptions } = useQuery({
    queryKey: ['roles'],
    queryFn: () => fetchRoleList(),
    enabled: isOpen, // only fetch when modal is open
  });

  const { data: hrCompanyOptions } = useQuery({
    queryKey: ['hrCompanyList'],
    queryFn: () => fetchHrCompanyList(),
    enabled: isOpen, // only fetch when modal is open
  });

  useEffect(() => {
    if (companyOptions.length && isOpen) {
      reset({
        ...initialValues,
        isDefaultCompanies: companyOptions.map(company => ({
          companyCode: company.Code,
          companyName: company.Description,
          isAllowed: false,
          isDefaultCompany: false,
        })),
      });
    }
    // eslint-disable-next-line
  }, [companyOptions, isOpen]);

  useEffect(() => {
    if (companyOptions.length && userData?.user && isOpen) {
      const { user } = userData;

      // Build canViewDashboard array based on flags
      const canViewDashboard = [];
      if (user.CanViewSelfService === 'Yes') canViewDashboard.push('S');
      if (user.CanViewHR === 'Yes') canViewDashboard.push('H');

      const payload = {
        id: user?.ID ?? 0,
        userName: user?.UserName ?? initialValues.userName,
        userFullName: user?.UserFullName ?? initialValues.userFullName,
        email: user?.Email ?? initialValues.email,
        roleId: user?.RoleID ?? initialValues.roleId,
        password: '', // Do not prefill password for security
        employeeId: user?.EmployeeID ?? initialValues.employeeId,
        isActive: user?.IsActive ?? initialValues.isActive,
        hrCoCd: user?.HRCoCd?.trim() ?? initialValues.hrCoCd,
        adAuthentication: user?.ADAuthentication === true,
        canViewOtherEmp: user?.CanViewOtherEmployee === true,
        isDefaultDashboard:
          user?.ISDefaultDashboard ?? initialValues.isDefaultDashboard,
        finCoCd: user?.finCoCd ?? initialValues.finCoCd,
        // Set companies: match companyOptions with user companies
        isDefaultCompanies: companyOptions?.map(company => {
          const userCompany = (user.companyList || []).find(
            c => c.Code === company.Code,
          );
          return {
            companyCode: company.Code,
            companyName: company.Description,
            isAllowed: !!userCompany?.Code,
            isDefaultCompany: userCompany?.ISDefault ?? false,
          };
        }),
        canViewDashboard,
      };
      reset(payload);
    }
  }, [userData, isOpen, reset, companyOptions]);

  const { mutate: createUserMutate, isLoading } = useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      toast.success('User updated successfully!', {
        className: 'toast-success',
      });
      reset();
      toggle();
      refetch();
      if (onUserUpdated) onUserUpdated();
    },
    onError: error => {
      toast.error(error?.message || 'Failed to create user', {
        className: 'toast-error',
      });
    },
  });

  const onSubmit = data => {
    const payload = {
      id: data.id || '0',
      userName: data.userName,
      password: data.password ? encode(data.password) : '',
      roleId: data.roleId,
      userFullName: data.userFullName,
      email: data.email,
      isActive: data.isActive === 'true',
      employeeImageUrl: '',
      employeeId: data.employeeId || '',
      isDefaultDashboard: data.isDefaultDashboard,
      isDefaultCompany:
        data.isDefaultCompanies.find(company => company.isDefaultCompany)
          ?.companyCode || '',
      canViewSelfService: data?.canViewDashboard?.includes('S') ? 'Yes' : '',
      canViewHR: data?.canViewDashboard?.includes('H') ? 'Yes' : '',
      canViewOtherEmp: data.canViewOtherEmp || false,
      hrCoCd: data.hrCoCd || '',
      adAuthentication: data.adAuthentication || false,
      finCoCd: data.finCoCd || '',
      deviceType: 'web',
      tokenFCM: '',
      isTwoFactor: 1,
      companyList: data.isDefaultCompanies
        .filter(company => company.isAllowed)
        .map(company => company.companyCode),
    };
    createUserMutate(payload);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const renderCompanyTable = () =>
    fields.map((company, index) => (
      <tr key={company.id || index}>
        <td>{company.companyName}</td>
        <td>
          <FormGroup switch>
            <Input
              type="switch"
              checked={company.isAllowed}
              onChange={() =>
                update(index, {
                  ...company,
                  isAllowed: !company.isAllowed,
                  isDefaultCompany:
                    company.isDefaultCompany && !!company.isAllowed
                      ? false
                      : company.isDefaultCompany,
                })
              }
            />
            <label htmlFor={`isAllowed-${index}`} className="ms-2">
              {company.isAllowed ? 'Yes' : 'No'}
            </label>
          </FormGroup>
        </td>
        <td>
          <FormGroup switch>
            <Input
              type="switch"
              checked={company.isDefaultCompany}
              disabled={!company.isAllowed}
              onChange={() => {
                // Set only this company as default, others as false
                fields.forEach((c, i) => {
                  update(i, {
                    ...c,
                    isDefaultCompany:
                      i === index ? !company.isDefaultCompany : false,
                  });
                });
              }}
            />
            <label htmlFor={`isDefaultCompany-${index}`} className="ms-2">
              {company.isDefaultCompany ? 'Yes' : 'No'}
            </label>
          </FormGroup>
        </td>
      </tr>
    ));

  return (
    <Offcanvas
      className="cu-offcanvas"
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
    >
      <OffcanvasHeader className="cu-offcanvas">Edit User</OffcanvasHeader>
      <Form onSubmit={handleSubmit(onSubmit)} className="edit-user-form">
        <OffcanvasBody className="cu-offcanvas-body">
          <Row className="gy-3">
            <Col md={6}>
              {/* Login Name */}
              <div className="d-flex flex-column gap-1">
                <Label>
                  Login Name<span className="text-danger">*</span>
                </Label>
                <Controller
                  name="userName"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      className={`form-control external-filter-input ${errors.userName && 'is-invalid'}`}
                      type="text"
                      placeholder="Enter Login Name"
                    />
                  )}
                />
                {errors.userName && (
                  <span className="text-danger">{errors.userName.message}</span>
                )}
              </div>
            </Col>
            <Col md={6}>
              {/* User Full Name */}
              <div className="d-flex flex-column gap-1">
                <Label>
                  User Full Name<span className="text-danger">*</span>
                </Label>
                <Controller
                  name="userFullName"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      className={`form-control external-filter-input ${errors.userFullName && 'is-invalid'}`}
                      type="text"
                      placeholder="Enter Full Name"
                    />
                  )}
                />
                {errors.userFullName && (
                  <span className="text-danger">
                    {errors.userFullName.message}
                  </span>
                )}
              </div>
            </Col>
            <Col md={6}>
              {/* Email */}
              <div className="d-flex flex-column gap-1">
                <Label>
                  Email<span className="text-danger">*</span>
                </Label>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      className={`form-control external-filter-input ${errors.email && 'is-invalid'}`}
                      type="email"
                      placeholder="Enter Email"
                    />
                  )}
                />
                {errors.email && (
                  <span className="text-danger">{errors.email.message}</span>
                )}
              </div>
            </Col>
            <Col md={6}>
              {/* Role */}
              <div className="d-flex flex-column gap-1">
                <Label>
                  Role<span className="text-danger">*</span>
                </Label>
                <Controller
                  name="roleId"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="select"
                      className={`form-select external-filter-input ${errors.roleId && 'is-invalid'}`}
                    >
                      <option value="">Select Role</option>
                      {roleOptions?.map(role => (
                        <option key={role.ID} value={role.ID}>
                          {role.Name}
                        </option>
                      ))}
                    </Input>
                  )}
                />
                {errors.roleId && (
                  <span className="text-danger">{errors.roleId.message}</span>
                )}
              </div>
            </Col>
            <Col md={6}>
              {/* Password */}
              <div className="d-flex flex-column gap-1">
                <Label>Password</Label>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <div className="input-icon-group">
                      <Icon
                        className="prefix-icon"
                        path={mdiKeyVariant}
                        size={0.7}
                      />
                      <Input
                        {...field}
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter Password"
                        className={`form-control input-icon external-filter-input ${errors.password && !field.value ? 'is-invalid' : ''}`}
                      />
                      <button
                        type="button"
                        className={`suffix-icon-btn ${errors.password && !field.value ? 'd-none' : ''}`}
                        onClick={togglePasswordVisibility}
                      >
                        <Icon
                          path={showPassword ? mdiEyeOffOutline : mdiEyeOutline}
                          size={0.7}
                        />
                      </button>
                    </div>
                  )}
                />
                {errors.password && (
                  <span className="text-danger">{errors.password.message}</span>
                )}
              </div>
            </Col>
            <Col md={6}>
              {/* Employee ID */}
              <div className="d-flex flex-column gap-1">
                <Label>Employee ID</Label>
                <Controller
                  name="employeeId"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      className={`form-control external-filter-input ${errors.employeeId && 'is-invalid'}`}
                      type="text"
                      placeholder="Enter Employee ID"
                    />
                  )}
                />
                {errors.employeeId && (
                  <span className="text-danger">
                    {errors.employeeId.message}
                  </span>
                )}
              </div>
            </Col>
            <Col md={6}>
              {/* Status */}
              <div className="d-flex flex-column gap-1">
                <Label>
                  Status<span className="text-danger">*</span>
                </Label>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      className={`form-select external-filter-input ${errors.isActive && 'is-invalid'}`}
                      type="select"
                    >
                      <option value="">Select Status</option>
                      {userStatusOptions.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </Input>
                  )}
                />
                {errors.isActive && (
                  <span className="text-danger">{errors.isActive.message}</span>
                )}
              </div>
            </Col>
            <Col md={6}>
              {/* HR Company */}
              <div className="d-flex flex-column gap-1">
                <Label>
                  HR Company<span className="text-danger">*</span>
                </Label>
                <Controller
                  name="hrCoCd"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      className={`form-select external-filter-input ${errors.hrCoCd && 'is-invalid'}`}
                      type="select"
                    >
                      <option value="">Select HR Company</option>
                      {hrCompanyOptions?.map(company => (
                        <option key={company?.Code} value={company?.Code}>
                          {company?.Description}
                        </option>
                      ))}
                    </Input>
                  )}
                />
                {errors.hrCoCd && (
                  <span className="text-danger">{errors.hrCoCd.message}</span>
                )}
              </div>
            </Col>
            <Col md={6}>
              {/* AD Auth and Can View Employee */}
              <Controller
                name="adAuthentication"
                control={control}
                render={({ field }) => (
                  <FormGroup check>
                    <Input
                      type="checkbox"
                      id="adAuthentication"
                      checked={!!field.value}
                      {...field}
                    />
                    <Label check htmlFor="adAuthentication" className="ms-2">
                      AD Authentication
                    </Label>
                  </FormGroup>
                )}
              />
            </Col>
            <Col md={6}>
              <Controller
                name="canViewOtherEmp"
                control={control}
                render={({ field }) => (
                  <FormGroup check>
                    <Input
                      type="checkbox"
                      id="canViewOtherEmp"
                      checked={!!field.value}
                      {...field}
                    />
                    <Label check htmlFor="canViewOtherEmp" className="ms-2">
                      Can View Other Employee
                    </Label>
                  </FormGroup>
                )}
              />
            </Col>
            <Col md={6}>
              {/* Is Default Dashboard */}
              <div className="d-flex flex-column gap-1">
                <Label>
                  Is Default Dashboard<span className="text-danger">*</span>
                </Label>
                <Controller
                  name="isDefaultDashboard"
                  control={control}
                  render={({ field }) => (
                    <div className="d-flex flex-row gap-3">
                      {defaultDashboard.map(dashboard => (
                        <FormGroup check key={dashboard.value}>
                          <Input
                            type="radio"
                            id={`default-dashboard-${dashboard.value}`}
                            name="isDefaultDashboard"
                            value={dashboard.value}
                            checked={field.value === dashboard.value}
                            onChange={e => {
                              field.onChange(e.target.value);
                            }}
                            className={
                              errors.isDefaultDashboard ? 'is-invalid' : ''
                            }
                          />
                          <Label
                            check
                            htmlFor={`default-dashboard-${dashboard.value}`}
                            className="ms-2"
                          >
                            {dashboard.label}
                          </Label>
                        </FormGroup>
                      ))}
                    </div>
                  )}
                />
                {errors.isDefaultDashboard && (
                  <span className="text-danger">
                    {errors.isDefaultDashboard.message}
                  </span>
                )}
              </div>
            </Col>
            <Col md={6}>
              <div className="d-flex flex-column gap-1">
                <Label>
                  Can View Dashboard<span className="text-danger">*</span>
                </Label>
                <Controller
                  name="canViewDashboard"
                  control={control}
                  render={({ field }) => (
                    <div className="d-flex flex-row gap-3">
                      {defaultDashboard.map(dashboard => (
                        <FormGroup check key={dashboard.value}>
                          <Input
                            type="checkbox"
                            id={`dashboard-${dashboard.value}`}
                            checked={
                              field.value?.includes(dashboard.value) || false
                            }
                            onChange={e => {
                              const currentValues = field.value || [];
                              if (e.target.checked) {
                                field.onChange([
                                  ...currentValues,
                                  dashboard.value,
                                ]);
                              } else {
                                field.onChange(
                                  currentValues.filter(
                                    val => val !== dashboard.value,
                                  ),
                                );
                              }
                            }}
                            className={
                              errors.canViewDashboard ? 'is-invalid' : ''
                            }
                          />
                          <Label
                            check
                            htmlFor={`dashboard-${dashboard.value}`}
                            className="ms-2"
                          >
                            {dashboard.label}
                          </Label>
                        </FormGroup>
                      ))}
                    </div>
                  )}
                />
                {errors.canViewDashboard && (
                  <span className="text-danger">
                    {errors.canViewDashboard.message}
                  </span>
                )}
              </div>
            </Col>
            {/* Company Table */}
            <Controller
              name="isDefaultCompanies"
              control={control}
              render={() => (
                <div className="table-responsive">
                  <Table className="cu-company-table">
                    <thead>
                      <tr>
                        <th className="fw-semibold">Company Name</th>
                        <th className="fw-semibold">Allow</th>
                        <th className="fw-semibold">Is Default Company</th>
                      </tr>
                    </thead>
                    <tbody>{renderCompanyTable()}</tbody>
                  </Table>
                  {/* Validation errors */}
                  {errors.isDefaultCompanies && (
                    <div className="text-danger">
                      {errors.isDefaultCompanies.message}
                    </div>
                  )}
                </div>
              )}
            />
          </Row>
        </OffcanvasBody>
        <div className="cu-footer d-flex flex-row justify-content-between gap-2 p-3">
          <Col>
            <button
              type="button"
              className="w-100 cu-cancel"
              onClick={async () => {
                toggle();
                reset();
                await trigger(); // Reset validation state
              }}
            >
              Cancel
            </button>
          </Col>
          <Col>
            <button
              type="submit"
              disabled={!isValid || isLoading}
              className="w-100 cu-add"
            >
              {isLoading ? 'Editing...' : 'Edit'}
            </button>
          </Col>
        </div>
      </Form>
    </Offcanvas>
  );
}

export default EditUser;
